import logger from '../utils/logger';
/**
 * PDF Preview V3 - Interface ultra élégante et complète
 * - Multi-vues : PDF, HTML, Markdown, Code
 * - Éditeur intégré avec syntax highlighting
 * - Export multi-formats
 * - Design minimaliste noir/blanc
 */

import React, { useState, useEffect, useCallback } from 'react';
import { exportToPDF, PDFOptions } from '../utils/pdfExportV3';
import { X, Download, ZoomIn, ZoomOut, Maximize2, Minimize2, ExternalLink, RefreshCw, FileText, Code, Eye, Edit3, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Bar, Pie, Scatter } from 'react-chartjs-2';
import { useAuth } from './auth/AuthProvider';
import { uploadDeepReportPDF } from '../lib/storage';
import { supabase } from '../lib/supabase';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

type ViewMode = 'pdf' | 'html' | 'markdown' | 'code';

// Composant pour les graphiques interactifs
const InteractiveChart = ({ type, data, title }: { type: string, data: any, title: string }) => {
  const normalizedData = Array.isArray(data) ? data.map((d: any) => {
    if (!d || typeof d !== 'object') return null;
    
    const labelKey = Object.keys(d).find(k => 
      typeof d[k] === 'string' || k.toLowerCase().includes('label') || k.toLowerCase().includes('name') || 
      k.toLowerCase().includes('mois') || k.toLowerCase().includes('produit')
    );
    const label = labelKey ? d[labelKey] : 'Sans nom';
    
    const valueKey = Object.keys(d).find(k => 
      typeof d[k] === 'number' || k.toLowerCase().includes('value') || k.toLowerCase().includes('ca') || 
      k.toLowerCase().includes('amount')
    );
    const value = valueKey ? (typeof d[valueKey] === 'number' ? d[valueKey] : parseFloat(d[valueKey]) || 0) : 0;
    
    return { label, value };
  }).filter(d => d && d.value > 0) : [];
  
  if (normalizedData.length === 0) {
    return null;
  }
  
  const cleanData = normalizedData;

  if (type === 'pie' || type === 'chart') {
    const chartData = {
      labels: cleanData.map((d: any) => d.label),
      datasets: [{
        data: cleanData.map((d: any) => d.value),
        backgroundColor: ['#8B5CF6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#8B5A2B', '#6B7280', '#EC4899', '#14B8A6', '#F97316'],
        borderWidth: 0,
        hoverBorderWidth: 2,
        hoverBorderColor: '#ffffff'
      }]
    };
    
    return (
      <div className="my-6 p-4 bg-theme-surface rounded-xl shadow-theme-md border border-theme-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-theme-text-primary">{title}</h3>
          <div className="text-xs text-theme-text-tertiary bg-theme-hover px-2 py-1 rounded">{cleanData.length} éléments</div>
        </div>
        <div style={{ height: '300px', display: 'flex', justifyContent: 'center' }}>
          <Pie data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' as const, labels: { boxWidth: 12, padding: 15 } } } }} />
        </div>
      </div>
    );
  }
  
  if (type === 'bar' || type === 'line') {
    const chartData = {
      labels: cleanData.map((d: any) => d.label),
      datasets: [{
        label: 'Valeur',
        data: cleanData.map((d: any) => d.value),
        backgroundColor: 'rgba(139, 92, 246, 0.8)',
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 1,
        borderRadius: 4
      }]
    };
    
    return (
      <div className="my-6 p-4 bg-theme-surface rounded-xl shadow-theme-md border border-theme-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-theme-text-primary">{title}</h3>
          <div className="text-xs text-theme-text-tertiary bg-theme-hover px-2 py-1 rounded">{cleanData.length} points</div>
        </div>
        <div style={{ height: '300px' }}>
          <Bar data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }} />
        </div>
      </div>
    );
  }
  
  if (type === 'scatter') {
    // Scatter chart avec données x/y
    const scatterData = Array.isArray(data) ? data.map((d: any, idx: number) => {
      const x = d.risk_probability || d.x || d.risque || idx;
      const y = d.financial_impact || d.y || d.impact || d.budget || d.value || 0;
      const label = d.phase || d.label || d.name || d.produit || `Point ${idx + 1}`;
      return { x, y, label };
    }).filter(d => typeof d.x === 'number' && typeof d.y === 'number') : [];
    
    if (scatterData.length === 0) return null;
    
    const chartData = {
      datasets: [{
        label: title,
        data: scatterData.map(d => ({ x: d.x, y: d.y })),
        backgroundColor: 'rgba(139, 92, 246, 0.6)',
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 2,
        pointRadius: 8,
        pointHoverRadius: 12
      }]
    };
    
    return (
      <div className="my-6 p-4 bg-theme-surface rounded-xl shadow-theme-md border border-theme-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-theme-text-primary">{title}</h3>
          <div className="text-xs text-theme-text-tertiary bg-theme-hover px-2 py-1 rounded">{scatterData.length} points</div>
        </div>
        <div style={{ height: '350px' }}>
          <Scatter 
            data={chartData} 
            options={{ 
              responsive: true, 
              maintainAspectRatio: false,
              plugins: { 
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (context: any) => {
                      const idx = context.dataIndex;
                      const point = scatterData[idx];
                      return `${point.label}: (${point.x}, ${point.y})`;
                    }
                  }
                }
              },
              scales: { 
                x: { 
                  title: { display: true, text: 'Risque (%)' },
                  beginAtZero: true 
                },
                y: { 
                  title: { display: true, text: 'Impact (€)' },
                  beginAtZero: true 
                }
              }
            }} 
          />
        </div>
      </div>
    );
  }
  
  return null;
};

interface PDFPreviewV3Props {
  content: string;
  options: PDFOptions;
  onClose?: () => void;
  onContentChange?: (newContent: string) => void;
  reportId?: string;
  sessionId?: string;
  onPDFUploaded?: (fileKey: string, downloadUrl: string) => void;
}

export default function PDFPreviewV3({ content, options, onClose, onContentChange, reportId, sessionId, onPDFUploaded }: PDFPreviewV3Props) {
  const { user } = useAuth();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('pdf');
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  const [copied, setCopied] = useState(false);
  const [pdfUploaded, setPdfUploaded] = useState(false);

  /**
   * Génère le PDF et l'upload vers Scaleway
   */
  const generatePDF = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      logger.log('Génération PDF V3...');
      const blob = await exportToPDF(content, options);
      
      // Révoquer l'ancien URL si existant
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      
      // Créer un nouvel URL pour preview local
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      logger.log('PDF V3 généré avec succès');

      // UPLOAD AUTOMATIQUE VERS SCALEWAY (DEV + PROD)
      if (user?.id && reportId && !pdfUploaded) {
        try {
          logger.log('[PDFPreview] Uploading to Scaleway...');
          const { fileKey, downloadUrl } = await uploadDeepReportPDF(
            blob,
            user.id,
            reportId,
            options.title || 'Deep Report'
          );
          logger.log(`[PDFPreview] PDF uploaded to Scaleway - Key: ${fileKey}`);
          
          // Mettre à jour le deep_report en DB avec le PDF Scaleway
          const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
          
          // SÉCURITÉ: Ajouter JWT pour authentification
          const { data: { session } } = await import('../lib/supabase').then(m => m.supabase.auth.getSession());
          const authHeaders: Record<string, string> = {
            'Content-Type': 'application/json'
          };
          
          if (session) {
            authHeaders['Authorization'] = `Bearer ${session.access_token}`;
          }
          
          const updateResponse = await fetch(`${API_URL}/api/deep-report/${reportId}/pdf`, {
            method: 'PUT',
            headers: authHeaders,
            body: JSON.stringify({
              userId: user.id,
              fileKey,
              downloadUrl,
              fileSize: blob.size
            })
          });
          
          if (updateResponse.ok) {
            logger.log(`[PDFPreview] Deep Report DB updated with PDF info`);
          } else {
            logger.warn(`[PDFPreview] Failed to update DB, but PDF is uploaded`);
          }
          
          setPdfUploaded(true);
          
          // Notifier le parent component
          if (onPDFUploaded) {
            onPDFUploaded(fileKey, downloadUrl);
          }
        } catch (uploadError) {
          logger.error('[PDFPreview] Upload to Scaleway failed:', uploadError);
          // Ne pas bloquer l'affichage si upload échoue
        }
      }
    } catch (err) {
      logger.error('Erreur génération PDF V3:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setIsLoading(false);
    }
  }, [content, options, pdfUrl, user, reportId, pdfUploaded, onPDFUploaded]);

  /**
   * Génère au montage et quand le contenu change
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      generatePDF();
    }, 500);

    return () => {
      clearTimeout(timer);
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [content]);

  /**
   * Télécharge le PDF
   */
  const handleDownload = async () => {
    if (!pdfUrl) return;
    
    try {
      const blob = await exportToPDF(content, options);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${options.title.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      logger.log('PDF téléchargé');
    } catch (err) {
      logger.error('Erreur téléchargement:', err);
    }
  };

  /**
   * Ouvre dans un nouvel onglet
   */
  const handleOpenNewTab = () => {
    if (pdfUrl) {
      // SÉCURITÉ #27: Ajouter noopener pour éviter accès window.opener
      const newWindow = window.open(pdfUrl, '_blank');
      if (newWindow) newWindow.opener = null;
    }
  };

  /**
   * Contrôles de zoom
   */
  const handleZoomIn = () => setZoom(prev => Math.min(200, prev + 25));
  const handleZoomOut = () => setZoom(prev => Math.max(50, prev - 25));
  const handleZoomReset = () => setZoom(100);

  /**
   * Copier le contenu dans le presse-papier
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editedContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Erreur copie:', err);
    }
  };

  /**
   * Sauvegarder les modifications
   */
  const handleSave = () => {
    if (onContentChange) {
      onContentChange(editedContent);
    }
    setEditMode(false);
    // Régénérer le PDF avec le nouveau contenu
    generatePDF();
  };

  /**
   * Exporter en HTML
   */
  const handleExportHTML = () => {
    const htmlContent = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${options.title}</title>
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 2rem; }
    h1, h2, h3 { color: #1a1a1a; }
    code { background: #f4f4f4; padding: 2px 6px; border-radius: 3px; }
    pre { background: #f4f4f4; padding: 1rem; border-radius: 6px; overflow-x: auto; }
  </style>
</head>
<body>
  <h1>${options.title}</h1>
  ${options.subtitle ? `<p><em>${options.subtitle}</em></p>` : ''}
  <div>${editedContent.replace(/\n/g, '<br>')}</div>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${options.title.replace(/\s+/g, '_')}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Exporter en Markdown
   */
  const handleExportMarkdown = () => {
    const blob = new Blob([editedContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${options.title.replace(/\s+/g, '_')}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div 
      className={`fixed inset-0 bg-theme-base/80 backdrop-blur-sm z-50 flex items-center justify-center transition-all ${
        isFullscreen ? 'p-0' : 'p-6'
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) {
          onClose();
        }
      }}
    >
      <div 
        className={`bg-theme-surface border border-theme-border flex flex-col transition-all duration-300 shadow-2xl ${
          isFullscreen ? 'w-full h-full' : 'w-[95vw] h-[95vh] max-w-7xl rounded-2xl'
        }`}
      >
        {/* HEADER - Ultra sobre avec onglets de vue */}
        <div className="flex-shrink-0 bg-theme-elevated border-b border-theme-border">
          {/* Titre et onglets */}
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center space-x-6">
          {/* Titre */}
              <div className="flex flex-col">
                <h3 className="text-theme-text-primary font-medium text-base tracking-tight">{options.title}</h3>
              {options.subtitle && (
                  <p className="text-theme-text-secondary text-xs mt-0.5">{options.subtitle}</p>
                )}
              </div>

              {/* Onglets de vue */}
              <div className="flex items-center space-x-1 bg-theme-hover rounded-lg p-1">
                <button
                  onClick={() => setViewMode('pdf')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center space-x-1.5 ${
                    viewMode === 'pdf'
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/30'
                      : 'text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-elevated'
                  }`}
                  title="Vue PDF"
                >
                  <FileText size={14} />
                  <span>PDF</span>
                </button>
                <button
                  onClick={() => setViewMode('html')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center space-x-1.5 ${
                    viewMode === 'html'
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/30'
                      : 'text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-elevated'
                  }`}
                  title="Vue HTML"
                >
                  <Eye size={14} />
                  <span>HTML</span>
                </button>
                <button
                  onClick={() => setViewMode('markdown')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center space-x-1.5 ${
                    viewMode === 'markdown'
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/30'
                      : 'text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-elevated'
                  }`}
                  title="Vue Markdown"
                >
                  <FileText size={14} />
                  <span>Markdown</span>
                </button>
                <button
                  onClick={() => setViewMode('code')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center space-x-1.5 ${
                    viewMode === 'code'
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/30'
                      : 'text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-elevated'
                  }`}
                  title="Code Source"
                >
                  <Code size={14} />
                  <span>Code</span>
                </button>
              </div>
          </div>

            {/* Contrôles - Icônes sobres */}
            <div className="flex items-center space-x-1">
            {/* Zoom Out */}
              <button
                onClick={handleZoomOut}
              disabled={zoom <= 50}
              className="p-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-hover rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Zoom arrière"
              >
              <ZoomOut size={18} />
              </button>

            {/* Zoom indicator */}
              <button
                onClick={handleZoomReset}
              className="px-3 py-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-hover rounded-lg transition-all duration-200 text-xs font-medium min-w-[60px]"
              title="Réinitialiser le zoom"
              >
                {zoom}%
              </button>

            {/* Zoom In */}
              <button
                onClick={handleZoomIn}
              disabled={zoom >= 200}
              className="p-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-hover rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Zoom avant"
              >
              <ZoomIn size={18} />
              </button>

            <div className="w-px h-6 bg-theme-border mx-2" />

            {/* Actualiser */}
            <button
              onClick={generatePDF}
              disabled={isLoading}
              className="p-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-hover rounded-lg transition-all duration-200 disabled:opacity-30"
              title="Actualiser"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
            </button>

            {/* Plein écran */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-hover rounded-lg transition-all duration-200"
              title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>

            {/* Ouvrir dans nouvel onglet */}
            {pdfUrl && (
              <button
                onClick={handleOpenNewTab}
                className="p-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-hover rounded-lg transition-all duration-200"
                title="Ouvrir dans un nouvel onglet"
              >
                <ExternalLink size={18} />
              </button>
            )}

            <div className="w-px h-6 bg-theme-border mx-2" />

            {/* Mode édition */}
            {viewMode !== 'pdf' && (
              <button
                onClick={() => {
                  setEditMode(!editMode);
                  if (editMode) handleSave();
                }}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  editMode
                    ? 'text-green-600 dark:text-green-400 bg-green-500/10 hover:text-green-700 dark:hover:text-green-300'
                    : 'text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-hover'
                }`}
                title={editMode ? 'Sauvegarder' : 'Éditer'}
              >
                <Edit3 size={18} />
              </button>
            )}

            {/* Copier */}
            <button
              onClick={handleCopy}
              className="p-2 text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-hover rounded-lg transition-all duration-200"
              title="Copier le contenu"
            >
              {copied ? <Check size={18} className="text-green-600 dark:text-green-400" /> : <Copy size={18} />}
            </button>

            <div className="w-px h-6 bg-theme-border mx-2" />

            {/* Menu Export */}
            <div className="relative group">
              <button
                className="p-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-500/10 rounded-lg transition-all duration-200"
                title="Exporter"
              >
                <Download size={18} />
              </button>
              {/* Dropdown Export */}
              <div className="absolute right-0 mt-2 w-48 bg-theme-surface border border-theme-border rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <button
                  onClick={handleDownload}
                  disabled={!pdfUrl || isLoading}
                  className="w-full px-4 py-2 text-left text-sm text-theme-text-secondary hover:bg-theme-hover rounded-t-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <FileText size={14} />
                  <span>Télécharger PDF</span>
                </button>
                <button
                  onClick={handleExportHTML}
                  className="w-full px-4 py-2 text-left text-sm text-theme-text-secondary hover:bg-theme-hover transition-colors flex items-center space-x-2"
                >
                  <Code size={14} />
                  <span>Exporter HTML</span>
                </button>
                <button
                  onClick={handleExportMarkdown}
                  className="w-full px-4 py-2 text-left text-sm text-theme-text-secondary hover:bg-theme-hover rounded-b-lg transition-colors flex items-center space-x-2"
                >
                  <FileText size={14} />
                  <span>Exporter Markdown</span>
                </button>
              </div>
            </div>

            {/* Fermer */}
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-theme-text-secondary hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all duration-200 ml-2"
                title="Fermer"
              >
                <X size={18} />
              </button>
            )}
          </div>
          </div>
        </div>

        {/* BODY - Multi-vues avec design sobre */}
        <div className="flex-1 overflow-hidden bg-theme-base relative">
          {/* Loading - Design minimaliste */}
          {isLoading && viewMode === 'pdf' && (
            <div className="absolute inset-0 flex items-center justify-center bg-theme-base/95 backdrop-blur-sm z-10">
              <div className="text-center">
                <div className="relative">
                  <div className="w-12 h-12 border-2 border-theme-border rounded-full mx-auto mb-4"></div>
                  <div className="w-12 h-12 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto absolute top-0 left-1/2 -translate-x-1/2"></div>
                </div>
                <p className="text-theme-text-secondary font-medium text-sm mt-6">Génération du PDF</p>
                <div className="flex items-center justify-center space-x-1 mt-3">
                  <div className="w-1.5 h-1.5 bg-purple-600 rounded-full animate-pulse"></div>
                  <div className="w-1.5 h-1.5 bg-purple-600 rounded-full animate-pulse delay-75"></div>
                  <div className="w-1.5 h-1.5 bg-purple-600 rounded-full animate-pulse delay-150"></div>
                </div>
              </div>
            </div>
          )}

          {/* Error - Design sobre */}
          {error && viewMode === 'pdf' && (
            <div className="absolute inset-0 flex items-center justify-center bg-theme-base/95 backdrop-blur-sm z-10">
              <div className="text-center max-w-md bg-theme-surface border border-theme-border p-8 rounded-xl shadow-2xl">
                <div className="w-12 h-12 border-2 border-red-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-red-600 dark:text-red-400 text-xl font-light">!</span>
                </div>
                <h4 className="text-base font-medium text-theme-text-primary mb-2">Échec de génération</h4>
                <p className="text-theme-text-secondary text-sm mb-6">{error}</p>
                <button
                  onClick={generatePDF}
                  className="px-5 py-2.5 bg-theme-hover hover:bg-theme-elevated text-theme-text-primary rounded-lg transition-all duration-200 text-sm font-medium border border-theme-border"
                >
                  Réessayer
                </button>
              </div>
            </div>
          )}

          {/* Vue PDF - Affichage optimal sans iframe */}
          {viewMode === 'pdf' && pdfUrl && !isLoading && !error && (
            <div className="w-full h-full overflow-auto custom-scrollbar bg-theme-base">
              <object
                data={`${pdfUrl}#view=FitH&toolbar=1&navpanes=0`}
                type="application/pdf"
                className="w-full h-full"
                style={{ 
                  minHeight: '100%',
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: 'top center',
                  transition: 'transform 0.2s ease-out'
                }}
              >
                <embed
                  src={`${pdfUrl}#view=FitH&toolbar=1&navpanes=0`}
                  type="application/pdf"
                  className="w-full h-full"
                />
              </object>
            </div>
          )}

          {/* Vue HTML (Rendu Markdown + Graphiques) */}
          {viewMode === 'html' && (() => {
            // FONCTION: Réparer JSON mal formé de graphiques (ULTRA ROBUSTE)
            const repairChartJSON = (jsonStr: string): string => {
              try {
                let cleaned = jsonStr
                  .replace(/\+(\d+)/g, '$1')
                  .replace(/,\s*}/g, '}')
                  .replace(/,\s*]/g, ']');
                
                try {
                  const parsed = JSON.parse(cleaned);
                  if (parsed.type && parsed.data) return cleaned;
                } catch (e) {}
                
                const typeMatch = cleaned.match(/"type"\s*:\s*"(bar|pie|line|chart|waterfall|scatter)"/);
                if (!typeMatch) return jsonStr;
                
                const chartType = typeMatch[1];
                const titleMatch = cleaned.match(/"title"\s*:\s*"([^"]+)"/);
                const title = titleMatch ? titleMatch[1] : 'Graphique';
                
                if (cleaned.includes('"data"')) return cleaned;
                
                const dataObjects: any[] = [];
                let depth = 0;
                let currentObj = '';
                let inString = false;
                let startedData = false;
                
                for (let i = 0; i < cleaned.length; i++) {
                  const char = cleaned[i];
                  const prevChar = i > 0 ? cleaned[i-1] : '';
                  
                  if (char === '"' && prevChar !== '\\') inString = !inString;
                  if (inString) {
                    if (startedData) currentObj += char;
                    continue;
                  }
                  
                  if (char === '{') {
                    depth++;
                    if (depth === 2) {
                      startedData = true;
                      currentObj = '{';
                    } else if (startedData) {
                      currentObj += char;
                    }
                  } else if (char === '}') {
                    if (startedData) currentObj += char;
                    depth--;
                    
                    if (depth === 1 && startedData && currentObj.length > 2) {
                      try {
                        const obj = JSON.parse(currentObj.replace(/\+(\d+)/g, '$1'));
                        dataObjects.push(obj);
                        currentObj = '';
                        startedData = false;
                      } catch (e) {}
                    }
                  } else if (startedData) {
                    currentObj += char;
                  }
                }
                
                if (dataObjects.length > 0) {
                  return JSON.stringify({ type: chartType, title, data: dataObjects });
                }
                
                return cleaned;
              } catch (e) {
                return jsonStr;
              }
            };
            
            // FONCTION: Extraire JSON complet en comptant les accolades (identique à FluidChat)
            const extractCompleteJSON = (text: string, startIndex: number, maxSearchLength: number = 50000): { json: string, endIndex: number } | null => {
              let braceCount = 0;
              let inString = false;
              let escapeNext = false;
              let jsonStart = -1;
              
              const searchEnd = Math.min(startIndex + maxSearchLength, text.length);
              
              for (let i = startIndex; i < searchEnd; i++) {
                const char = text[i];
                
                if (escapeNext) {
                  escapeNext = false;
                  continue;
                }
                
                if (char === '\\') {
                  escapeNext = true;
                  continue;
                }
                
                if (char === '"') {
                  inString = !inString;
                  continue;
                }
                
                if (inString) continue;
                
                if (char === '{') {
                  if (braceCount === 0) jsonStart = i;
                  braceCount++;
                } else if (char === '}') {
                  braceCount--;
                  if (braceCount === 0 && jsonStart >= 0) {
                    return {
                      json: text.substring(jsonStart, i + 1),
                      endIndex: i + 1
                    };
                  }
                }
              }
              
              return null;
            };
            
            // Types de graphiques supportés (identique à FluidChat)
            const supportedChartTypes = [
              'bar', 'pie', 'line', 'scatter',
              'chart', 'horizontal_bar', 'waterfall', 'area', 'gantt',
              'doughnut', 'polarArea',
              'stacked_bar', 'radar', 'bubble'
            ];
            const chartTypePattern = `(${supportedChartTypes.join('|')})`;
            
            let allCharts: any[] = [];
            let contentWithoutCharts = editedContent;
            const chartMatches: Array<{ json: string, startIndex: number, endIndex: number }> = [];
            
            // Pattern 1: SPECIFICATION JSON: {...}
            const specPattern = /SPECIFICATION\s+JSON:\s*/gi;
            const allSpecMatches = Array.from(editedContent.matchAll(specPattern));
            
            for (const specMatch of allSpecMatches) {
              if (!specMatch || specMatch.index === undefined) continue;
              
              const searchStart = specMatch.index + specMatch[0].length;
              const result = extractCompleteJSON(editedContent, searchStart);
              
              if (result) {
                if (new RegExp(`"type"\\s*:\\s*"${chartTypePattern}"`, 'i').test(result.json)) {
                  const repairedJSON = repairChartJSON(result.json);
                  
                  const alreadyAdded = chartMatches.some(m => 
                    Math.abs(m.startIndex - specMatch.index!) < 20 || 
                    Math.abs(m.endIndex - result.endIndex) < 20
                  );
                  
                  if (!alreadyAdded) {
                    chartMatches.push({
                      json: repairedJSON,
                      startIndex: specMatch.index,
                      endIndex: result.endIndex
                    });
                  }
                }
              }
            }
            
            // Pattern 2: ```json\n{...}\n```
            const codeBlockPattern = /```json\s*/gi;
            const allCodeMatches = Array.from(editedContent.matchAll(codeBlockPattern));
            
            for (const codeMatch of allCodeMatches) {
              if (!codeMatch || codeMatch.index === undefined) continue;
              
              const searchStart = codeMatch.index + codeMatch[0].length;
              const result = extractCompleteJSON(editedContent, searchStart);
              
              if (result) {
                if (new RegExp(`"type"\\s*:\\s*"${chartTypePattern}"`, 'i').test(result.json)) {
                  const repairedJSON = repairChartJSON(result.json);
                  const endMarker = editedContent.indexOf('```', result.endIndex);
                  const finalEndIndex = endMarker > -1 ? endMarker + 3 : result.endIndex;
                  
                  const alreadyAdded = chartMatches.some(m => 
                    Math.abs(m.startIndex - codeMatch.index!) < 20 || 
                    Math.abs(m.endIndex - finalEndIndex) < 20
                  );
                  
                  if (!alreadyAdded) {
                    chartMatches.push({
                      json: repairedJSON,
                      startIndex: codeMatch.index,
                      endIndex: finalEndIndex
                    });
                  }
                }
              }
            }
            
            // Pattern 3: ```chart\n{...}\n```
            const chartBlockPattern = /```chart\s*/gi;
            const allChartBlockMatches = Array.from(editedContent.matchAll(chartBlockPattern));
            
            for (const chartBlockMatch of allChartBlockMatches) {
              if (!chartBlockMatch || chartBlockMatch.index === undefined) continue;
              
              const searchStart = chartBlockMatch.index + chartBlockMatch[0].length;
              const result = extractCompleteJSON(editedContent, searchStart);
              
              if (result) {
                if (new RegExp(`"type"\\s*:\\s*"${chartTypePattern}"`, 'i').test(result.json)) {
                  const repairedJSON = repairChartJSON(result.json);
                  const endMarker = editedContent.indexOf('```', result.endIndex);
                  const finalEndIndex = endMarker > -1 ? endMarker + 3 : result.endIndex;
                  
                  const alreadyAdded = chartMatches.some(m => 
                    Math.abs(m.startIndex - chartBlockMatch.index!) < 20 || 
                    Math.abs(m.endIndex - finalEndIndex) < 20
                  );
                  
                  if (!alreadyAdded) {
                    chartMatches.push({
                      json: repairedJSON,
                      startIndex: chartBlockMatch.index,
                      endIndex: finalEndIndex
                    });
                  }
                }
              }
            }
            
            // Pattern 4: JSON nu dans le texte
            const nakedJsonPattern = new RegExp(`\\{\\s*"type"\\s*:\\s*"${chartTypePattern}"[\\s\\S]*?"data"\\s*:\\s*\\[[\\s\\S]*?\\][\\s\\S]*?\\}`, 'gi');
            let nakedMatch: RegExpExecArray | null;
            while ((nakedMatch = nakedJsonPattern.exec(editedContent)) !== null) {
              try {
                if (!nakedMatch) continue;
                const result = extractCompleteJSON(editedContent, nakedMatch.index);
                if (result && new RegExp(`"type"\\s*:\\s*"${chartTypePattern}"`, 'i').test(result.json)) {
                  const repairedJSON = repairChartJSON(result.json);
                  const alreadyAdded = chartMatches.some(m => 
                    Math.abs(m.startIndex - nakedMatch!.index) < 10
                  );
                  if (!alreadyAdded) {
                    chartMatches.push({
                      json: repairedJSON,
                      startIndex: nakedMatch.index,
                      endIndex: result.endIndex
                    });
                  }
                }
              } catch (e) {
                // Ignorer les erreurs
              }
            }
            
            // Trier par position (ordre croissant pour préserver l'ordre)
            chartMatches.sort((a, b) => a.startIndex - b.startIndex);
            
            // Parser les graphiques et les stocker avec leur position
            const chartsWithPosition: Array<{ chart: any, position: number }> = [];
            
            for (const match of chartMatches) {
              try {
                const chartSpec = JSON.parse(match.json);
                
                if (chartSpec.type && supportedChartTypes.includes(chartSpec.type.toLowerCase()) && chartSpec.data) {
                  // Vérifier qu'on n'a pas déjà ajouté ce graphique (éviter doublons)
                  const isDuplicate = chartsWithPosition.some(item => 
                    item.chart.title === chartSpec.title && 
                    item.chart.type === chartSpec.type
                  );
                  
                  if (!isDuplicate) {
                    chartsWithPosition.push({
                      chart: {
                        ...chartSpec,
                        id: `preview-chart-${chartsWithPosition.length}`
                      },
                      position: match.startIndex
                    });
                  }
                }
              } catch (e) {
                // Ignorer les erreurs de parsing
              }
            }
            
            // Supprimer tous les graphiques du contenu markdown (ordre décroissant pour éviter problèmes d'indices)
            const sortedMatches = [...chartMatches].sort((a, b) => b.startIndex - a.startIndex);
            for (const match of sortedMatches) {
              contentWithoutCharts = contentWithoutCharts.substring(0, match.startIndex) + 
                contentWithoutCharts.substring(match.endIndex);
            }
            
            // Trier les graphiques par ordre d'apparition dans le texte original
            chartsWithPosition.sort((a, b) => a.position - b.position);
            allCharts = chartsWithPosition.map(item => item.chart);
            
            return (
              <div className="w-full h-full overflow-auto p-8 custom-scrollbar">
                <div className="max-w-4xl mx-auto bg-theme-surface dark:bg-gray-900 p-8 rounded-lg shadow-xl border border-theme-border">
                  <div className="prose prose-slate dark:prose-invert max-w-none prose-headings:text-theme-text-primary dark:prose-headings:text-gray-100 prose-p:text-theme-text-primary dark:prose-p:text-gray-200 prose-strong:text-theme-text-primary dark:prose-strong:text-gray-100 prose-li:text-theme-text-primary dark:prose-li:text-gray-200 prose-code:text-purple-700 dark:prose-code:text-purple-300 prose-pre:bg-gray-900 dark:prose-pre:bg-gray-800 prose-pre:text-gray-100 prose-blockquote:text-gray-600 dark:prose-blockquote:text-gray-400 prose-table:text-theme-text-primary dark:prose-table:text-gray-200 prose-th:bg-gray-100 dark:prose-th:bg-gray-800 prose-th:text-gray-900 dark:prose-th:text-gray-100 prose-td:bg-white dark:prose-td:bg-gray-900 prose-td:text-gray-900 dark:prose-td:text-gray-100 prose-td:border-gray-300 dark:prose-td:border-gray-700">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                    >
                      {contentWithoutCharts}
                    </ReactMarkdown>
                  </div>
                  
                  {/* Graphiques détectés */}
                  {allCharts.length > 0 && (
                    <div className="mt-6 space-y-4">
                      {allCharts.map((chart) => (
                        <InteractiveChart
                          key={chart.id}
                          type={chart.type}
                          data={chart.data}
                          title={chart.title || 'Graphique'}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Vue Markdown (Brut ou Édition) */}
          {viewMode === 'markdown' && (
            <div className="w-full h-full overflow-auto p-8 custom-scrollbar">
              <div className="max-w-4xl mx-auto">
                {editMode ? (
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-full min-h-[600px] bg-theme-surface text-theme-text-secondary p-6 rounded-lg border border-theme-border focus:border-purple-500/50 focus:outline-none font-mono text-sm leading-relaxed resize-none"
                    placeholder="Éditer le contenu Markdown..."
                  />
                ) : (
                  <div className="bg-theme-surface text-theme-text-secondary p-6 rounded-lg border border-theme-border font-mono text-sm leading-relaxed whitespace-pre-wrap">
                    {editedContent}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Vue Code Source */}
          {viewMode === 'code' && (
            <div className="w-full h-full overflow-auto p-8 custom-scrollbar">
              <div className="max-w-4xl mx-auto">
                {editMode ? (
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-full min-h-[600px] bg-theme-surface text-theme-text-secondary p-6 rounded-lg border border-theme-border focus:border-purple-500/50 focus:outline-none font-mono text-xs leading-relaxed resize-none"
                    style={{ tabSize: 2 }}
                    placeholder="Éditer le code source..."
                  />
                ) : (
                  <pre className="bg-theme-surface text-theme-text-secondary p-6 rounded-lg border border-theme-border overflow-x-auto">
                    <code className="font-mono text-xs leading-relaxed">
                      {editedContent}
                    </code>
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER - Design sobre et minimaliste */}
        <div className="flex-shrink-0 bg-gradient-to-r bg-theme-surface to-black border-t border-theme-border px-4 py-3 flex items-center justify-between text-xs">
          <div className="flex items-center space-x-4">
            <span className="text-theme-secondary font-light">A4 · 210×297mm</span>
            <div className="w-px h-3 bg-theme-surface" />
            <span className="text-theme-secondary font-light">Haute qualité</span>
            <div className="w-px h-3 bg-theme-surface" />
            <span className="text-theme-secondary font-light">Graphiques natifs</span>
          </div>
          <div>
            {isLoading ? (
              <span className="text-orange-600 font-light flex items-center space-x-2">
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse"></div>
                <span>Génération</span>
              </span>
            ) : pdfUrl ? (
              <span className="text-green-400 font-light flex items-center space-x-2">
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                <span>Prêt</span>
              </span>
            ) : (
              <span className="text-theme-tertiary font-light">En attente</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

